import { ArrowLeft, ExternalLink } from 'lucide-react'
import { Link } from 'react-router'
import { Alert } from '../../common/Alert'
import { Button } from '../../common/Button'
import { StatePanel } from '../../common/StatePanel'
import { StatusBadge } from '../../common/StatusBadge'
import { ChangeSet } from '../../patterns/ChangeSet'
import { ContextReferenceList } from '../../patterns/ContextReferenceList'
import styles from '../../CommandCenterApprovals.module.css'
import { buildPostGroupDecision } from './postGroupDecisionModel'

export function ApprovalDecisionPanel({
  group,
  detail,
  loading = false,
  error,
  actionError,
  deciding = false,
  onRetry,
  onApprove,
  onReject,
  onBack,
  startFocusRef,
}) {
  if (!group) {
    return (
      <section className={styles.decisionPanel} aria-label="Approval decision">
        <StatePanel compact state="empty" title="Select an approval" description="Choose a pending distributed post to review its impact." />
      </section>
    )
  }

  const decision = buildPostGroupDecision(group, detail)

  return (
    <section className={styles.decisionPanel} aria-labelledby="approval-decision-title">
      <Button className={styles.mobileBack} variant="ghost" onClick={onBack}>
        <ArrowLeft size={17} aria-hidden="true" />
        Pending approvals
      </Button>

      <header className={styles.decisionHeader}>
        <div className={styles.decisionHeading}>
          <div className={styles.decisionTitleLine}>
            <h2 ref={startFocusRef} id="approval-decision-title" tabIndex={-1}>{decision.label}</h2>
            <StatusBadge auto>{detail?.approval_status || group.approval_status}</StatusBadge>
          </div>
          <span>{decision.postCount} member post{decision.postCount === 1 ? '' : 's'}</span>
        </div>
        <Link className={styles.detailLink} to={`/command-center/post-groups/${group.group_id}`}>
          Full details
          <ExternalLink size={15} aria-hidden="true" />
        </Link>
      </header>

      {loading ? (
        <StatePanel compact state="loading" title="Loading approval details" description="Decision controls remain unavailable until review context is ready." />
      ) : error ? (
        <StatePanel
          compact
          state="error"
          title="Approval details could not load"
          description="Nothing can be approved or rejected until the complete distributed post is available."
          onRetry={onRetry}
        />
      ) : !decision.readiness.ready ? (
        <StatePanel
          compact
          state="error"
          title="Approval details are incomplete"
          description={decision.readiness.reason}
          onRetry={onRetry}
        />
      ) : (
        <>
          <ContextReferenceList items={decision.context} />
          <ChangeSet
            items={decision.changes}
            description="The approval applies to every member post in this group."
          />

          <section className={styles.consequence} aria-labelledby="approval-consequence-title">
            <h3 id="approval-consequence-title">Decision impact</h3>
            <p>{decision.approvalConsequence}</p>
            <p>{decision.rejectionConsequence}</p>
          </section>

          {actionError ? <Alert variant="error">{actionError}</Alert> : null}

          <div className={styles.decisionActions} aria-label="Approval actions">
            <Button variant="danger" disabled={deciding} onClick={onReject}>Reject</Button>
            <Button loading={deciding} onClick={onApprove}>Approve distributed post</Button>
          </div>
        </>
      )}
    </section>
  )
}
